import { Injectable } from '@angular/core';
import { BaseBackend } from '@shared/apis/servers/base-backend';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DealerBackendServer extends BaseBackend {
  getDealers(builder): Observable<any> {
    return this.get('/dealer', { params: builder });
  }

  exportDealers(builder): Observable<any> {
    return this.post('/dealer/export', builder);
  }

  getDealerByHqBpNo(hqBpNo): Observable<any> {
    return this.get(`/dealer/hq/${hqBpNo}`);
  }

  getUnAssignedDealers(builder): Observable<any> {
    return this.get('/dealer/unassigned', { params: builder });
  }

  getUnAssignedLocationAliases(builder): Observable<any> {
    return this.get('/dealer/unassigned-location-aliases', { params: builder });
  }

  getNewDealers(builder): Observable<any> {
    return this.get('/dealer/request', { params: builder });
  }

  getDealer(bpNo): Observable<any> {
    return this.get(`/dealer/${bpNo}`);
  }

  checkDuplicateEmail(mainEmail, bpNo?): Observable<any> {
    return this.get(`/dealer-email-check/${mainEmail}?bpNo=${bpNo}`);
  }

  getHQDealers(): Observable<any> {
    return this.get('/dealer/hq-dealers');
  }

  getSelectedHqDealerList(): Observable<any> {
    return this.get('/dealer/selected-hq-dealers');
  }

  getDealerCodes(): Observable<any> {
    return this.get('/dealer/code');
  }

  getDealerCodesWithState(): Observable<any> {
    return this.get('/dealer/code-with-state');
  }

  getStores(bpNo): Observable<any> {
    return this.get(`/dealer/${bpNo}/stores`);
  }

  getAllStores(bpNo): Observable<any> {
    return this.get(`/dealer/${bpNo}/all-stores`);
  }

  getMyStores(params?): Observable<any> {
    return this.get(`/dealer/my-stores`, { params });
  }

  getFilterStores(body): Observable<any> {
    return this.post('/dealer/filter-stores', body);
  }

  createHqDealer(dealer): Observable<any> {
    return this.post('/dealer-register', dealer);
  }

  createDealer(dealer): Observable<any> {
    return this.post('/dealer', dealer);
  }

  updateDealer(bpNo, dealer): Observable<any> {
    return this.patch(`/dealer/${bpNo}`, dealer);
  }

  deleteRequestedDealer(bpNo): Observable<any> {
    return this.delete(`/dealer/${bpNo}`);
  }

  updateRequestStatus(bpNo, onboarding): Observable<any> {
    return this.patch(`/dealer/${bpNo}/onboarding`, onboarding);
  }

  createDealerContact(bpNo, dealerContact): Observable<any> {
    return this.post(`/dealer/${bpNo}/contact`, dealerContact);
  }

  updateDealerContact(bpNo, contactId, dealerContact): Observable<any> {
    return this.patch(`/dealer/${bpNo}/contact/${contactId}`, dealerContact);
  }

  deleteDealerContact(bpNo, contactId): Observable<any> {
    return this.delete(`/dealer/${bpNo}/contact/${contactId}`);
  }

  saveDealerContact(bpNo, dealerContact): Observable<any> {
    const { contactId } = dealerContact;
    if (contactId) {
      return this.patch(`/dealer/${bpNo}/contact/${contactId}`, dealerContact);
    }
    return this.post(`/dealer/${bpNo}/contact`, dealerContact);
  }

  getDealerContactForTicket(bpNo, contactId): Observable<any> {
    return this.get(`/dealer/${bpNo}/contact/${contactId}/ticket`);
  }

  createLocationAlias(bpNo, locationAlias): Observable<any> {
    return this.post(`/dealer/${bpNo}/location-alias`, locationAlias);
  }

  updateLocationAlias(bpNo, locationAliasId, locationAlias): Observable<any> {
    return this.patch(
      `/dealer/${bpNo}/location-alias/${locationAliasId}`,
      locationAlias,
    );
  }

  deleteLocationAlias(bpNo, locationAliasId): Observable<any> {
    return this.delete(`/dealer/${bpNo}/location-alias/${locationAliasId}`);
  }

  saveLocationAlias(bpNo, locationAlias): Observable<any> {
    const { locationAliasId } = locationAlias;
    if (locationAliasId) {
      return this.patch(
        `/dealer/${bpNo}/location-alias/${locationAliasId}`,
        locationAlias,
      );
    }
    return this.post(`/dealer/${bpNo}/location-alias`, locationAlias);
  }

  checkValidBpNo(bpNo): Observable<any> {
    return this.get(`/dealer/valid/${bpNo}`);
  }

  saveContactBp(contactData): Observable<any> {
    return this.post('/dealer/saveContactBp', contactData);
  }

  updateContactBp(contactData): Observable<any> {
    return this.post('/dealer/updateContactBp', contactData);
  }

  getAllDealers(company): Observable<any> {
    return this.get('/dealer/all-dealers', company);
  }

  // B2B
  getBpDealers(): Observable<any> {
    return this.get('/dealer/bp-dealers');
  }

  getCpDealers(hqBpNo = null): Observable<any> {
    return this.get('/dealer/cp-dealers', { params: { hqBpNo } });
  }

  updateBp(bpNo, bpInfo): Observable<any> {
    return this.patch(`/dealer/b2b/${bpNo}`, bpInfo);
  }

  createBp(bpInfo): Observable<any> {
    return this.post('/dealer/b2b', bpInfo);
  }

  getCpByBp(hpBpNo): Observable<any> {
    return this.get(`/dealer/b2b/${hpBpNo}/cps`);
  }

  getCpsByBp(builder): Observable<any> {
    return this.get('/dealer/b2b/bp-cps', { params: builder });
  }

  deleteCp(hqBpNo, bpNo): Observable<any> {
    return this.delete(`/dealer/b2b/${hqBpNo}/cp/${bpNo}`);
  }

  getDealersByZipCode(zipcode): Observable<any> {
    return this.get(`/dealer/b2b/${zipcode}`);
  }

  saveDealerAddressFavorite(storeBpNo, newAddress): Observable<any> {
    return this.patch(
      `/dealer/${storeBpNo}/save-dealer-address-favorite`,
      newAddress,
    );
  }

  deleteDealerAddressFavorite(storeBpNo, newAddress): Observable<any> {
    return this.patch(
      `/dealer/${storeBpNo}/delete-dealer-address-favorite`,
      newAddress,
    );
  }

  getDealerActivitySummary(data): Observable<any> {
    return this.get(`/dealer/dealer-activity-data`, { params: data });
  }
}
